import { type FirebaseAllAvailabilityById } from "../../model/firebaseAllAvailability";
import {
  type ByRestaurantId,
  type DiscountAvailabilityById,
} from "../../model/types";
import type { NonEmptyArray } from "../../types";

/**
 * Opaque per-store handle. {@link createRestaurantStore} assigns one object per
 * Easy Peasy store instance; Immer never replaces it when `byId` merges.
 */
export type DeriveCacheIdentity = object;

/** Stable cache key for an ordered date list (discount / next-day derives). */
export const getDatesKey = (dates: readonly string[]): string =>
  dates.join("\0");

export type DiscountAvailabilityCacheEntry = {
  sourceRef: FirebaseAllAvailabilityById[number] | undefined;
  datesKey: string;
  globalKey: string;
  /**
   * Placeholder used when Firebase has no snapshot yet.
   * `undefined` = still loading; `null` = resolved empty (no matching sessions).
   */
  unresolvedPlaceholder: null | undefined;
  /** Filtered, deduped, time-sorted slots for this restaurant. */
  availability: DiscountAvailabilityById[number];
};

export type DiscountAvailabilityBundle = {
  byId: DiscountAvailabilityById;
  nextDay: ByRestaurantId<boolean>;
};

export type DiscountPreviousBundleResult = {
  contextKey: string;
  restaurantIds: number[];
  discountDatesToShowById: ByRestaurantId<NonEmptyArray<string>>;
  byIdRef: FirebaseAllAvailabilityById;
  restaurantsByIdRef: object;
  result: DiscountAvailabilityBundle;
};

export type NextDayCacheEntry = {
  sourceRef: FirebaseAllAvailabilityById[number] | undefined;
  datesKey: string;
  sessionsKey: string;
  people: number;
  value: boolean;
};

const restaurantCacheByStore = new WeakMap<
  DeriveCacheIdentity,
  Map<number, DiscountAvailabilityCacheEntry>
>();
const nextDayCacheByStore = new WeakMap<
  DeriveCacheIdentity,
  Map<number, NextDayCacheEntry>
>();
const previousDiscountBundleByStore = new WeakMap<
  DeriveCacheIdentity,
  DiscountPreviousBundleResult
>();

export const getDiscountRestaurantCache = (
  identity: DeriveCacheIdentity,
): Map<number, DiscountAvailabilityCacheEntry> => {
  let cache = restaurantCacheByStore.get(identity);
  if (!cache) {
    cache = new Map();
    restaurantCacheByStore.set(identity, cache);
  }
  return cache;
};

export const getNextDayRestaurantCache = (
  identity: DeriveCacheIdentity,
): Map<number, NextDayCacheEntry> => {
  let cache = nextDayCacheByStore.get(identity);
  if (!cache) {
    cache = new Map();
    nextDayCacheByStore.set(identity, cache);
  }
  return cache;
};

export const getPreviousDiscountBundle = (
  identity: DeriveCacheIdentity,
): DiscountPreviousBundleResult | undefined =>
  previousDiscountBundleByStore.get(identity);

export const setPreviousDiscountBundle = (
  identity: DeriveCacheIdentity,
  value: DiscountPreviousBundleResult,
): void => {
  previousDiscountBundleByStore.set(identity, value);
};

export const clearDeriveAvailabilityCaches = (
  identity: DeriveCacheIdentity,
): void => {
  restaurantCacheByStore.delete(identity);
  nextDayCacheByStore.delete(identity);
  previousDiscountBundleByStore.delete(identity);
};
